import { createApi, fetchBaseQuery } from '@reduxjs/toolkit/query/react';
import type {
  Conversation,
  CreateConversationParams,
  UpdateConversationParams,
  PaginatedResponse,
  ListConversationsParams,
  Message,
  CreateMessageParams,
} from '@/types';
import { getAccessToken } from '@/utils/jwt';

const AGENT_MCP_URL = import.meta.env.VITE_AGENT_MCP_URL as string | undefined;

/** Map backend conversation shape (conversation_id) to frontend (id). */
interface BackendConversation {
  conversation_id: string;
  user_id: string;
  title: string;
  created_at: string;
  updated_at: string;
  messages: Record<string, unknown>[];
  feedback: Record<string, unknown> | null;
}

function mapConversation(c: BackendConversation): Conversation {
  return {
    id: c.conversation_id,
    title: c.title,
    createdAt: c.created_at,
    updatedAt: c.updated_at,
    messageCount: c.messages.length,
  };
}

export const conversationApi = createApi({
  reducerPath: 'conversationApi',
  baseQuery: fetchBaseQuery({
    baseUrl: AGENT_MCP_URL ?? '/agent',
    prepareHeaders: (headers) => {
      const token = getAccessToken();
      if (token) {
        headers.set('Authorization', `Bearer ${token}`);
      }
      headers.set('Content-Type', 'application/json');
      return headers;
    },
  }),
  tagTypes: ['Conversation', 'Message'],
  endpoints: (builder) => ({
    listConversations: builder.query<PaginatedResponse<Conversation>, ListConversationsParams>({
      query: (params) => {
        const page = params.page ?? 1;
        const pageSize = params.pageSize ?? 20;
        return {
          url: '/conversations',
          params: {
            limit: pageSize,
            offset: (page - 1) * pageSize,
            search: params.search,
          },
        };
      },
      transformResponse: (
        response: { success: boolean; conversations: BackendConversation[]; count: number },
        _meta,
        params,
      ): PaginatedResponse<Conversation> => {
        const page = params.page ?? 1;
        const pageSize = params.pageSize ?? 20;
        return {
          items: response.conversations.map(mapConversation),
          total: response.count,
          page,
          pageSize,
          hasMore: page * pageSize < response.count,
        };
      },
      providesTags: ['Conversation'],
    }),

    getConversation: builder.query<Conversation, string>({
      query: (id) => `/conversations/${id}`,
      transformResponse: (response: {
        success: boolean;
        conversation: BackendConversation;
      }): Conversation => mapConversation(response.conversation),
      providesTags: (_result, _error, id) => [{ type: 'Conversation', id }],
    }),

    createConversation: builder.mutation<Conversation, CreateConversationParams>({
      query: (params) => ({
        url: '/conversations',
        method: 'POST',
        body: params,
      }),
      transformResponse: (response: {
        success: boolean;
        conversation: BackendConversation;
      }): Conversation => mapConversation(response.conversation),
      invalidatesTags: ['Conversation'],
    }),

    updateConversation: builder.mutation<Conversation, UpdateConversationParams>({
      query: ({ id, ...body }) => ({
        url: `/conversations/${id}`,
        method: 'PATCH',
        body,
      }),
      transformResponse: (response: {
        success: boolean;
        conversation: BackendConversation;
      }): Conversation => mapConversation(response.conversation),
      invalidatesTags: ['Conversation'],
    }),

    deleteConversation: builder.mutation<undefined, string>({
      query: (id) => ({
        url: `/conversations/${id}`,
        method: 'DELETE',
      }),
      invalidatesTags: ['Conversation'],
    }),

    getMessages: builder.query<Message[], string>({
      query: (conversationId) => `/conversations/${conversationId}/messages`,
      providesTags: (_result, _error, id) => [{ type: 'Message', id }],
    }),

    sendMessage: builder.mutation<Message, CreateMessageParams>({
      query: ({ conversationId, content }) => ({
        url: `/conversations/${conversationId}/messages`,
        method: 'POST',
        body: { content },
      }),
      invalidatesTags: (_result, _error, { conversationId }) => [
        { type: 'Message', id: conversationId },
      ],
    }),
  }),
});

export const {
  useListConversationsQuery,
  useGetConversationQuery,
  useCreateConversationMutation,
  useUpdateConversationMutation,
  useDeleteConversationMutation,
  useGetMessagesQuery,
  useSendMessageMutation,
} = conversationApi;
